/**
 * Custom hook for user management action handlers.
 * Extracted from UsersManagement to keep component files under 500 lines.
 *
 * @returns Action handlers, state setters, and loading states for user management
 */

import { useState, useCallback, useEffect } from 'react';
import { coreAPI, UserProfile, Role, UserManagementLog, UserSitePermission } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { toast } from 'sonner';

/**
 * Merges a single-row write response back into the cached permission list.
 *
 * The write endpoints return UserSitePermissionSerializer, which carries only
 * the stored columns. The tree fields the Related Companies tab renders from
 * — group_id, group_name, parent_group_id, parent_group_name, is_group_entry,
 * role_name — are synthesised by the list endpoint and absent from that
 * payload. Spreading the response *over* the existing row keeps every key the
 * response omits, so the row keeps its place in the Group -> Division -> Site
 * tree instead of falling out of its group until the modal is reopened.
 *
 * @param rows - The current cached permission rows
 * @param siteId - Site whose row was written
 * @param updated - The write response body
 * @returns A new array with that row merged
 */
function mergeSitePermission(
  rows: UserSitePermission[],
  siteId: number,
  updated: UserSitePermission,
): UserSitePermission[] {
  return rows.map(p => (p.site === siteId ? { ...p, ...updated } : p));
}

interface UseUserActionsReturn {
  // Data
  users: UserProfile[];
  availableRoles: Role[];
  activityLogs: UserManagementLog[];
  userSitePermissions: UserSitePermission[];
  // State
  loading: boolean;
  actionLoading: boolean;
  loadingSitePermissions: boolean;
  error: string | null;
  selectedUser: UserProfile | null;
  selectedRoleId: number | null;
  savingPermission: number | null;
  savingDivision: number | null;
  userDetailTab: 'profile' | 'companies';
  // Modal visibility
  showUserDetails: boolean;
  showChangeRoleDialog: boolean;
  showChangeCompanyDialog: boolean;
  setShowChangeCompanyDialog: (open: boolean) => void;
  showActivityLogDialog: boolean;
  showBulkApproveDialog: boolean;
  showBulkInviteDialog: boolean;
  // Setters
  setError: (error: string | null) => void;
  setSelectedUser: (user: UserProfile | null) => void;
  setSelectedRoleId: (id: number | null) => void;
  setUserDetailTab: (tab: 'profile' | 'companies') => void;
  setShowUserDetails: (show: boolean) => void;
  setShowChangeRoleDialog: (show: boolean) => void;
  setShowActivityLogDialog: (show: boolean) => void;
  setShowBulkApproveDialog: (show: boolean) => void;
  setShowBulkInviteDialog: (show: boolean) => void;
  // Actions
  fetchUsers: (scope?: { view_level?: string; group?: number | null; company?: number | null; site?: number | null }) => Promise<void>;
  handleToggleActive: (userProfile: UserProfile) => Promise<void>;
  handleSafeDelete: (userProfile: UserProfile) => Promise<void>;
  handleChangeRole: () => Promise<void>;
  handleRelinkCompany: (companyId: number) => Promise<void>;
  handleToggleAdmin: (userProfile: UserProfile, isAdmin: boolean) => Promise<void>;
  handleSendInvitation: (userProfile: UserProfile) => Promise<void>;
  handleViewActivityLog: (userProfile: UserProfile) => Promise<void>;
  openChangeRoleDialog: (userProfile: UserProfile) => void;
  openChangeCompanyDialog: (userProfile: UserProfile) => void;
  openUserDetails: (userProfile: UserProfile) => Promise<void>;
  handleToggleSiteLink: (siteId: number, isLinked: boolean) => Promise<void>;
  handleToggleSitePermission: (siteId: number, permission: string, value: boolean) => Promise<void>;
  handleAssignDivision: (
    groupId: number,
    opts: { isLinked: boolean; modules?: Record<string, boolean>; applyModulesToAll?: boolean }
  ) => Promise<void>;
  handleApproveUser: (userId: number) => Promise<void>;
  handleRejectUser: (userId: number) => Promise<void>;
  handleBulkApprove: (userIds: number[]) => Promise<void>;
  handleBulkSendInvitations: (profileIds: number[]) => Promise<void>;
  fetchAvailableRoles: () => Promise<void>;
}

/**
 * Hook encapsulating all user management API actions and related state.
 *
 * @returns Object with action handlers, data, and UI state for user management
 */
export function useUserActions(): UseUserActionsReturn {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [activityLogs, setActivityLogs] = useState<UserManagementLog[]>([]);
  const [userSitePermissions, setUserSitePermissions] = useState<UserSitePermission[]>([]);

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [loadingSitePermissions, setLoadingSitePermissions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingPermission, setSavingPermission] = useState<number | null>(null);
  // CR-6076 AC3: tracks which division (group id) is mid-save for the
  // bulk link/unlink toggle in the Related Companies tree.
  const [savingDivision, setSavingDivision] = useState<number | null>(null);

  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [userDetailTab, setUserDetailTab] = useState<'profile' | 'companies'>('profile');

  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showChangeRoleDialog, setShowChangeRoleDialog] = useState(false);
  const [showChangeCompanyDialog, setShowChangeCompanyDialog] = useState(false);
  const [showActivityLogDialog, setShowActivityLogDialog] = useState(false);
  const [showBulkApproveDialog, setShowBulkApproveDialog] = useState(false);
  const [showBulkInviteDialog, setShowBulkInviteDialog] = useState(false);

  const fetchUsers = useCallback(async (
    scope?: { view_level?: string; group?: number | null; company?: number | null; site?: number | null }
  ) => {
    try {
      setLoading(true); setError(null);
      // CR-5374: pass the active view-context scope so the backend can
      // narrow the Customer / Supplier tab cohorts to the user's
      // hierarchy (group / division / site). When no scope is set the
      // backend preserves the platform-wide rollup.
      const params: Record<string, string | number | boolean> = { page_size: 1000 };
      if (scope?.view_level) params['view_level'] = scope.view_level;
      if (scope?.group) params['group'] = scope.group;
      if (scope?.company) params['company'] = scope.company;
      if (scope?.site) params['site'] = scope.site;
      // The backend caps page_size at 100 (StandardResultsSetPagination
      // .max_page_size), so a single request silently truncates tenants
      // with more users — everyone past the cap vanished from this page,
      // unfindable even by search (all filtering here is client-side).
      // Walk every page until the API reports no next page.
      const all: UserProfile[] = [];
      for (let page = 1; page <= 50; page += 1) {
        const response = await coreAPI.getUserProfiles({ ...params, page });
        all.push(...(response.data.results || []));
        if (!response.data.next) break;
      }
      setUsers(all);
    } catch (err: unknown) {
      console.error('Error fetching users:', err);
      setError(getApiErrorMessage(err, 'Failed to load users'));
    } finally { setLoading(false); }
  }, []);

  const fetchAvailableRoles = useCallback(async () => {
    try {
      const response = await coreAPI.getAvailableRoles();
      setAvailableRoles(response.data.roles || []);
    } catch (err: unknown) {
      console.error('Error fetching roles:', err);
    }
  }, []);

  useEffect(() => {
    void fetchAvailableRoles();
  }, [fetchAvailableRoles]);

  const handleToggleActive = useCallback(async (userProfile: UserProfile) => {
    try {
      setActionLoading(true); setError(null);
      const response = await coreAPI.toggleUserActive(userProfile.id);
      const newIsActive = response.data.is_active;
      await fetchUsers();
      setSelectedUser((prev) =>
        prev?.id === userProfile.id ? { ...prev, user: { ...prev.user, is_active: newIsActive } } : prev
      );
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to update user status'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  // CR-5312: backend decides delete-vs-deactivate based on the user's
  // login/audit footprint; we just surface the right toast.
  const handleSafeDelete = useCallback(async (userProfile: UserProfile) => {
    try {
      setActionLoading(true); setError(null);
      const response = await coreAPI.safeDeleteUser(userProfile.id);
      const { deleted, deactivated, message } = response.data;
      if (deleted) toast.success(message || `User ${userProfile.user.email} deleted`);
      else if (deactivated) toast.success(message || `User ${userProfile.user.email} deactivated`);
      else toast.message(message || 'No change');
      await fetchUsers();
      // Close any open detail modal pointing at the deleted user.
      setSelectedUser((prev) => (prev?.id === userProfile.id && deleted ? null : prev));
      if (deleted) setShowUserDetails(false);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to delete user'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  // CR-5294: surface success/failure via toast — the page-level error
  // banner sits behind the modal backdrop, which made failed role changes
  // look like the popup did nothing.
  const handleChangeRoleImpl = async () => {
    if (!selectedUser || !selectedRoleId) return;
    try {
      setActionLoading(true); setError(null);
      const response = await coreAPI.changeUserRole(selectedUser.id, selectedRoleId);
      await fetchUsers();
      setSelectedUser(response.data.user);
      setShowChangeRoleDialog(false); setSelectedRoleId(null);
      toast.success(response.data.message || 'Role updated');
    } catch (err: unknown) {
      const message = getApiErrorMessage(err, 'Failed to change user role');
      setError(message);
      toast.error(message);
    } finally { setActionLoading(false); }
  };

  const handleRelinkCompany = async (companyId: number) => {
    if (!selectedUser) return;
    try {
      setActionLoading(true); setError(null);
      const response = await coreAPI.relinkUserCompany(selectedUser.id, companyId);
      await fetchUsers();
      setSelectedUser(response.data.user);
      setShowChangeCompanyDialog(false);
      toast.success(response.data.message || 'User relinked');
      if (response.data.role_cleared) {
        toast.info('The previous role no longer fits the new company type — assign a new role.');
      }
    } catch (err: unknown) {
      const message = getApiErrorMessage(err, 'Failed to relink user');
      setError(message);
      toast.error(message);
    } finally { setActionLoading(false); }
  };

  const handleToggleAdmin = useCallback(async (userProfile: UserProfile, isAdmin: boolean) => {
    try {
      setActionLoading(true); setError(null);
      await coreAPI.toggleUserAdmin(userProfile.id, isAdmin);
      await fetchUsers();
      setSelectedUser((prev) =>
        prev?.id === userProfile.id
          ? { ...prev, user: { ...prev.user, is_superuser: isAdmin, is_staff: isAdmin } }
          : prev
      );
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to update admin status'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  const handleSendInvitation = useCallback(async (userProfile: UserProfile) => {
    try {
      setActionLoading(true); setError(null);
      const response = await coreAPI.sendInvitationEmail(userProfile.id);
      toast.success(`Invitation email sent to ${userProfile.user.email}`);
      if (response.data.user) {
        const updated = response.data.user;
        setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
        setSelectedUser((prev) => (prev?.id === updated.id ? updated : prev));
      } else {
        await fetchUsers();
      }
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to send invitation email'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  const handleViewActivityLog = useCallback(async (userProfile: UserProfile) => {
    try {
      setSelectedUser(userProfile); setActionLoading(true);
      const response = await coreAPI.getUserManagementLogs(userProfile.id);
      setActivityLogs(response.data || []);
      setShowActivityLogDialog(true);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to load activity logs'));
    } finally { setActionLoading(false); }
  }, []);

  const openChangeRoleDialog = useCallback((userProfile: UserProfile) => {
    setSelectedUser(userProfile);
    setSelectedRoleId(userProfile.role);
    setShowChangeRoleDialog(true);
  }, []);

  const openChangeCompanyDialog = useCallback((userProfile: UserProfile) => {
    setSelectedUser(userProfile);
    setShowChangeCompanyDialog(true);
  }, []);

  const openUserDetails = useCallback(async (userProfile: UserProfile) => {
    setSelectedUser(userProfile); setUserDetailTab('profile');
    setShowUserDetails(true); setUserSitePermissions([]);
    setLoadingSitePermissions(true);
    // CR-6029: dropped the parallel coreAPI.getSites() call. It required
    // IsHelixUser and 403'd for customer admins, which rejected the
    // Promise.all and left the Related Companies tab stuck on
    // "Loading company access…". The site-permissions endpoint already
    // returns every sibling site in the user's group, so the extra
    // sites payload was both unused (stored in a discarded state) and
    // the sole reason the tab was breaking for non-Helix admins.
    try {
      const permissionsResponse = await coreAPI.getUserSitePermissions(userProfile.id);
      setUserSitePermissions(permissionsResponse.data || []);
    } catch (err) {
      console.error('Error fetching site permissions:', err);
      setUserSitePermissions([]);
    } finally {
      setLoadingSitePermissions(false);
    }
  }, []);

  const handleToggleSiteLink = async (siteId: number, isLinked: boolean) => {
    if (!selectedUser) return;
    setSavingPermission(siteId);
    try {
      const response = await coreAPI.updateUserSitePermission(selectedUser.id, siteId, {
        is_linked: isLinked, is_active: isLinked, can_purchasing: isLinked,
      });
      setUserSitePermissions(prev => mergeSitePermission(prev, siteId, response.data));
      await fetchUsers();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to update site link'));
    } finally { setSavingPermission(null); }
  };

  const handleToggleSitePermission = async (siteId: number, permission: string, value: boolean) => {
    if (!selectedUser) return;
    setSavingPermission(siteId);
    try {
      // CR-7076: the CR-5903 mutual-exclusion clearing is retired. The
      // Administrator/Approval toggles are hidden (their flags are DORMANT —
      // no backend check reads them), Read Only supersedes the role rather
      // than conflicting with it, and silently zeroing the dormant flags
      // would destroy recorded per-site admin intent the data-repair ticket
      // preserves.
      const payload: Record<string, boolean> = { [permission]: value };
      const response = await coreAPI.updateUserSitePermission(selectedUser.id, siteId, payload);
      setUserSitePermissions(prev => mergeSitePermission(prev, siteId, response.data));
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to update permission'));
    } finally { setSavingPermission(null); }
  };

  // CR-6076 AC3: bulk link/unlink every site in a division for the user, with
  // optional module cascade. Refetches the whole permission list afterwards
  // because one call updates many site rows.
  const handleAssignDivision = async (
    groupId: number,
    opts: { isLinked: boolean; modules?: Record<string, boolean>; applyModulesToAll?: boolean }
  ) => {
    if (!selectedUser) return;
    setSavingDivision(groupId);
    try {
      await coreAPI.assignUserGroupAccess(selectedUser.id, {
        group_id: groupId,
        is_linked: opts.isLinked,
        apply_modules_to_all: opts.applyModulesToAll,
        ...(opts.modules || {}),
      });
      const resp = await coreAPI.getUserSitePermissions(selectedUser.id);
      setUserSitePermissions(resp.data || []);
      await fetchUsers();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to update division access'));
    } finally { setSavingDivision(null); }
  };

  const handleApproveUser = useCallback(async (userId: number) => {
    try {
      setActionLoading(true); setError(null);
      await coreAPI.approveUser(userId);
      await fetchUsers();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to approve user'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  const handleRejectUser = useCallback(async (userId: number) => {
    try {
      setActionLoading(true); setError(null);
      await coreAPI.rejectUser(userId);
      toast.success('User rejected and deactivated');
      await fetchUsers();
      setSelectedUser(null);
      setShowUserDetails(false);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to reject user'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  const handleBulkApprove = useCallback(async (userIds: number[]) => {
    try {
      setActionLoading(true); setError(null);
      await coreAPI.bulkApproveUsers(userIds);
      await fetchUsers();
      setShowBulkApproveDialog(false);
      toast.success('Selected pending users approved');
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to approve users'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  const handleBulkSendInvitations = useCallback(async (profileIds: number[]) => {
    if (profileIds.length === 0) return;
    try {
      setActionLoading(true); setError(null);
      let ok = 0;
      let failed = 0;
      for (const id of profileIds) {
        try {
          const response = await coreAPI.sendInvitationEmail(id);
          ok += 1;
          if (response.data.user) {
            const updated = response.data.user;
            setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
          }
        } catch {
          failed += 1;
        }
      }
      await fetchUsers();
      if (failed === 0) {
        toast.success(`Invitation emails sent (${ok})`);
      } else if (ok === 0) {
        toast.error(`Failed to send ${failed} invitation(s)`);
      } else {
        toast.error(`Sent ${ok} invitation(s); ${failed} failed`);
      }
      setShowBulkInviteDialog(false);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Bulk invitation failed'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  return {
    users, availableRoles, activityLogs, userSitePermissions,
    loading, actionLoading, loadingSitePermissions, error, selectedUser, selectedRoleId,
    savingPermission, savingDivision, userDetailTab,
    showUserDetails, showChangeRoleDialog, showActivityLogDialog, showBulkApproveDialog,
    showChangeCompanyDialog, setShowChangeCompanyDialog,
    showBulkInviteDialog,
    setError, setSelectedUser, setSelectedRoleId, setUserDetailTab,
    setShowUserDetails, setShowChangeRoleDialog, setShowActivityLogDialog, setShowBulkApproveDialog,
    setShowBulkInviteDialog,
    fetchUsers,
    handleToggleActive, handleSafeDelete, handleChangeRole: handleChangeRoleImpl, handleToggleAdmin,
    handleRelinkCompany,
    handleSendInvitation, handleViewActivityLog,
    openChangeRoleDialog, openUserDetails,
    openChangeCompanyDialog,
    handleToggleSiteLink, handleToggleSitePermission, handleAssignDivision,
    handleApproveUser, handleRejectUser, handleBulkApprove, handleBulkSendInvitations,
    fetchAvailableRoles,
  };
}
